// Phase 1D -- Your Business: Company Context as a model of the business,
// not a document. Structured objects (who we are, products, customers,
// markets, positioning, competitors, growth priorities), each with its
// provenance:
//   INFERRED  -- Martian inferred it (website research or the industry you
//                gave) and you haven't confirmed it yet
//   CONFIRMED -- you confirmed or wrote it (Company Context source "user")
//   MISSING   -- Martian doesn't know yet
// (KNOWN -- from connected data -- appears once a connection supplies facts.)
// Primary action: Confirm / Correct. Competitors: suggested -> evidence ->
// Approve / Not a competitor; never added silently (Phase 0 P0.4).
import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle, PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ARCState, GLIMState, IntelligenceChain } from '@/brand';
import { CONTEXT_FIELD_GUIDANCE, FieldReviewHint, contextProvenance, saveErrorMessage } from '@/pages/brand/CompanyContextPage';
import { businessProfileAPI, companyContextAPI, competitorsAPI, competitorSuggestionsAPI, somPromptsAPI } from '@/lib/api';
import { useFounderWorkspace, useFounderPath } from '../FounderWorkspace';
import { Chip, Disclosure, Eyebrow, Loading, PageHeader, PrimaryButton, SecondaryButton, TextButton, EmptyState, formatDate } from '../ui';

const ESSENTIAL_FIELDS = ['company_name', 'business_summary', 'primary_market', 'audience', 'positioning'];

const PROVENANCE = {
  confirmed: { label: 'Confirmed', tone: 'good', title: 'You confirmed or wrote this' },
  inferred: { label: 'Inferred', tone: 'warn', title: "Martian inferred this and you haven't confirmed it yet" },
  missing: { label: 'Missing', tone: 'neutral', title: "Martian doesn't know this yet" },
};

// The shared Company Context rule, so the chip, the Confirm action, the
// Review prompt and the counts below always agree.
const provenanceOf = contextProvenance;

function firstSentence(text) {
  if (!text) return '';
  const m = text.match(/^(.+?[.!?])(\s|$)/);
  return m ? m[1] : text;
}

function Fact({ label, field, fieldKey, children, onSave, onConfirm, list = false, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const prov = provenanceOf(field);
  const p = PROVENANCE[prov];
  const start = () => {
    setDraft(list ? (field?.value || []).join('\n') : (field?.value || ''));
    setEditing(true);
  };
  // A failed save keeps the editor open with the draft (onSave returns false).
  const save = async () => {
    if (saving) return;
    const value = list ? draft.split(/[\n,]/).map((s) => s.trim()).filter(Boolean) : draft.trim();
    setSaving(true);
    try {
      const saved = await onSave(fieldKey, value);
      if (saved !== false) setEditing(false);
    } finally {
      setSaving(false);
    }
  };
  const confirm = async () => {
    if (confirming) return;
    setConfirming(true);
    try { await onConfirm(fieldKey, field.value); } finally { setConfirming(false); }
  };
  return (
    <section className="py-6 border-t border-[var(--border)] grid md:grid-cols-[180px_1fr] gap-2 md:gap-6" data-testid={testId || `business-${fieldKey}`}>
      <div>
        <div className="flex items-start justify-between gap-2">
          <Eyebrow>{label}</Eyebrow>
          <FieldReviewHint
            meta={{ key: fieldKey, label }}
            field={field}
            guidance={CONTEXT_FIELD_GUIDANCE[fieldKey]}
            onConfirm={onConfirm}
            align="responsive"
          />
        </div>
        <div className="mt-2"><Chip tone={p.tone} title={p.title} testId={`provenance-${fieldKey}`}>{p.label}</Chip></div>
      </div>
      <div className="min-w-0">
        {editing ? (
          <div>
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={list ? 4 : 3} aria-label={label}
              className="w-full rounded-[var(--radius-input)] border border-[var(--border)] bg-[var(--surface-sunken)] px-3 py-2 text-[14.5px] leading-relaxed focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]" />
            {list && <p className="mt-1 text-[12px] text-[var(--text-tertiary)]">One per line, or separated by commas.</p>}
            <div className="mt-3 flex gap-3"><PrimaryButton onClick={save} disabled={saving} testId={`save-${fieldKey}`}>{saving ? 'Saving…' : 'Save'}</PrimaryButton><TextButton onClick={() => setEditing(false)} disabled={saving}>Cancel</TextButton></div>
          </div>
        ) : (
          <>
            {prov === 'missing' ? <p className="text-[14.5px] text-[var(--text-tertiary)]">Martian doesn't know this yet.</p> : children}
            <div className="mt-3 flex flex-wrap gap-4">
              {prov === 'inferred' && <TextButton tone="accent" onClick={confirm} disabled={confirming} testId={`confirm-${fieldKey}`}><CheckCircle size={14} /> {confirming ? 'Confirming…' : 'Confirm'}</TextButton>}
              <TextButton onClick={start} testId={`correct-${fieldKey}`}><PencilSimple size={14} /> {prov === 'missing' ? 'Add' : 'Correct'}</TextButton>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function Competitors({ workspaceId }) {
  const [approved, setApproved] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const loadSeq = useRef(0); // only the latest load (this workspace) may set state
  const load = useCallback(() => {
    const seq = ++loadSeq.current;
    const current = () => seq === loadSeq.current;
    competitorsAPI.list(workspaceId).then((r) => current() && setApproved(r.data || [])).catch(() => current() && setApproved([]));
    competitorSuggestionsAPI.list(workspaceId, { refresh: true })
      .then((r) => current() && setSuggested((r.data?.suggestions || []).filter((s) => s.status === 'suggested')))
      .catch(() => current() && setSuggested([]));
  }, [workspaceId]);
  useEffect(() => { load(); }, [load]);
  const decide = async (s, ok) => {
    setBusy(true);
    try { if (ok) await competitorSuggestionsAPI.approve(workspaceId, s.id); else await competitorSuggestionsAPI.reject(workspaceId, s.id); load(); }
    catch { toast.error("Couldn't save that — please try again"); } finally { setBusy(false); }
  };
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await competitorsAPI.create(workspaceId, { brand_name: name.trim(), domain: '', competitor_type: 'primary' }); setName(''); setAdding(false); load(); }
    catch { toast.error("Couldn't add that competitor"); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    setBusy(true);
    try { await competitorsAPI.remove(id); load(); }
    catch { toast.error("Couldn't remove that competitor — please try again"); }
    finally { setBusy(false); }
  };

  return (
    <section className="py-6 border-t border-[var(--border)] grid md:grid-cols-[180px_1fr] gap-2 md:gap-6" data-testid="business-competitors">
      <div>
        <Eyebrow>Competitors</Eyebrow>
        <p className="mt-2 text-[12.5px] text-[var(--text-tertiary)] max-w-[22ch]">Only the ones you approve are tracked.</p>
      </div>
      <div className="min-w-0">
        {approved === null ? <p className="text-[13px] text-[var(--text-tertiary)]">Loading…</p> : (
          <div className="flex flex-wrap gap-2">
            {approved.length === 0 && <p className="text-[14.5px] text-[var(--text-tertiary)]">None approved yet.</p>}
            {approved.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1.5 h-8 pl-3 pr-1.5 rounded-full border border-[var(--border)] text-[13.5px]" data-testid="competitor-approved">
                {c.brand_name}
                <button type="button" onClick={() => remove(c.id)} disabled={busy} aria-label={`Remove ${c.brand_name}`} className="p-1 rounded-full text-[var(--text-tertiary)] hover:text-[var(--error)]"><Trash size={12} /></button>
              </span>
            ))}
          </div>
        )}
        {suggested.length > 0 && (
          <div className="mt-5" data-testid="competitor-suggestions">
            <Eyebrow tone="accent">Suggested by Martian</Eyebrow>
            <ul className="mt-2 divide-y divide-[var(--border)]">
              {suggested.map((s) => (
                <li key={s.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="competitor-suggestion">
                  <div className="min-w-0 flex-1">
                    <p className="text-[14.5px] font-semibold">{s.name}</p>
                    <p className="text-[12.5px] text-[var(--text-secondary)]">{s.reason}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <SecondaryButton onClick={() => decide(s, true)} disabled={busy} testId="competitor-approve">Approve</SecondaryButton>
                    <TextButton onClick={() => decide(s, false)} disabled={busy} testId="competitor-reject">Not a competitor</TextButton>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-4">
          {adding ? (
            <div className="flex flex-wrap items-center gap-2">
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="Competitor name" aria-label="Competitor name"
                className="h-10 min-w-[200px] flex-1 rounded-[var(--radius-input)] border border-[var(--border)] bg-[var(--surface-sunken)] px-3 text-[14px] focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]" />
              <SecondaryButton onClick={add} disabled={busy}>Add</SecondaryButton>
              <TextButton onClick={() => setAdding(false)}>Cancel</TextButton>
            </div>
          ) : <TextButton onClick={() => setAdding(true)} testId="competitor-add"><Plus size={14} /> Add a competitor</TextButton>}
        </div>
      </div>
    </section>
  );
}

export default function BusinessPage() {
  const { workspaceId, displayName, loadingClients, refreshSummary } = useFounderWorkspace();
  const fp = useFounderPath();
  const [ctx, setCtx] = useState(null);
  const [profile, setProfile] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [confirmingAll, setConfirmingAll] = useState(false);
  // Responses for a workspace the founder has since left are never shown.
  const activeWorkspace = useRef(workspaceId);
  activeWorkspace.current = workspaceId;

  useEffect(() => {
    if (!workspaceId) return undefined;
    let cancelled = false;
    setCtx(null); setProfile(null); setQuestions(null);
    companyContextAPI.get(workspaceId).then((r) => { if (!cancelled) setCtx(r.data); }).catch(() => { if (!cancelled) setCtx({}); });
    businessProfileAPI.get(workspaceId).then((r) => { if (!cancelled) setProfile(r.data); }).catch(() => { if (!cancelled) setProfile({}); });
    somPromptsAPI.list(workspaceId).then((r) => { if (!cancelled) setQuestions(r.data?.prompts || []); }).catch(() => { if (!cancelled) setQuestions([]); });
    return () => { cancelled = true; };
  }, [workspaceId]);

  // One save path for edits, single confirmations and "Confirm all": the
  // real Company Context API, which records every non-empty value it saves
  // as source "user" (confirmed). Returns false on failure.
  const persist = async (payload, successMessage) => {
    const requestedFor = workspaceId;
    try {
      const res = await companyContextAPI.update(requestedFor, payload);
      if (activeWorkspace.current !== requestedFor) return true;
      setCtx(res.data);
      businessProfileAPI.get(requestedFor).then((r) => { if (activeWorkspace.current === requestedFor) setProfile(r.data); }).catch(() => {});
      refreshSummary();
      toast.success(successMessage);
      return true;
    } catch (e) {
      toast.error(saveErrorMessage(e));
      return false;
    }
  };
  const save = (key, value) => persist({ [key]: value }, 'Saved');
  const confirm = (key, value) => save(key, value);

  if (loadingClients || (workspaceId && (!ctx || !profile || questions === null))) return <Loading />;
  if (!workspaceId) return <EmptyState title="No business yet" body="Add your website and Martian will build this for you." action={<PrimaryButton to="/onboarding">Add your website</PrimaryButton>} />;

  const FIELDS = ['company_name', 'business_summary', 'audience', 'primary_market', 'markets_served', 'positioning', 'growth_priorities', 'additional_context'];
  const inferredKeys = FIELDS.filter((k) => provenanceOf(ctx[k]) === 'inferred');
  const missingEssentialKeys = ESSENTIAL_FIELDS.filter((k) => provenanceOf(ctx[k]) === 'missing');
  const needsAttention = inferredKeys.length > 0 || missingEssentialKeys.length > 0;
  const confirmAll = async () => {
    if (confirmingAll) return;
    const payload = {};
    inferredKeys.forEach((k) => { payload[k] = ctx[k].value; });
    setConfirmingAll(true);
    try { await persist(payload, 'Thanks — Martian will treat these as confirmed'); } finally { setConfirmingAll(false); }
  };
  const approvedQuestions = (questions || []).filter((q) => q.watchlist_status === 'approved').length;
  const lastUpdated = FIELDS.map((k) => ctx[k]?.updated_at).filter(Boolean).sort().pop();

  return (
    <div data-testid="business-page">
      <PageHeader
        eyebrow="Your Business"
        title={`How Martian understands ${displayName || 'your business'}`}
        summary={inferredKeys.length
          ? `Martian inferred ${inferredKeys.length === 1 ? 'one of these details' : `${inferredKeys.length} of these details`}. Confirm what's right and correct what isn't — everything Martian prepares is checked against it.`
          : 'Everything Martian prepares is checked against this.'}
        primary={inferredKeys.length > 0 && <PrimaryButton onClick={confirmAll} disabled={confirmingAll} testId="business-confirm-all">{confirmingAll ? 'Confirming…' : `Confirm ${inferredKeys.length === FIELDS.length ? 'all' : `${inferredKeys.length} fact${inferredKeys.length === 1 ? '' : 's'}`}`}</PrimaryButton>}
      />

      {needsAttention && (
        <div className="mb-8 flex items-start gap-3 rounded-[var(--radius-card)] border border-[var(--accent-border)] bg-[var(--accent-light)] p-4" data-testid="business-review-guidance">
          <GLIMState state="curious" size={44} showLabel={false} announce={false} />
          <div className="min-w-0">
            <p className="text-[13.5px] font-semibold text-[var(--text-primary)]">A few details deserve your attention.</p>
            <p className="mt-1 text-[12.5px] leading-relaxed text-[var(--text-secondary)]">
              {inferredKeys.length ? `${inferredKeys.length} inferred detail${inferredKeys.length === 1 ? '' : 's'} still need your review.` : 'The core business profile has gaps Martian could not verify.'}
              {missingEssentialKeys.length ? ` Add or correct ${missingEssentialKeys.map((k) => ({ company_name: 'the brand name', business_summary: 'the business description', primary_market: 'the main market', audience: 'the audience', positioning: 'the positioning' }[k])).join(', ')} if you know them.` : ''}
              {' '}Use the field-level guidance to confirm or correct the facts. Missing information is not treated as truth.
            </p>
          </div>
        </div>
      )}

      <Fact label="Name" field={ctx.company_name} fieldKey="company_name" onSave={save} onConfirm={confirm}>
        <p className="text-[16px] font-semibold">{ctx.company_name?.value}</p>
      </Fact>

      <Fact label="Who we are" field={ctx.business_summary} fieldKey="business_summary" onSave={save} onConfirm={confirm}>
        <p className="font-display text-[18px] font-semibold leading-snug max-w-[56ch] text-balance">{firstSentence(ctx.business_summary?.value)}</p>
        {ctx.business_summary?.value && ctx.business_summary.value !== firstSentence(ctx.business_summary.value) && (
          <div className="mt-2"><Disclosure label="Full description"><p className="text-[14px] text-[var(--text-secondary)] leading-relaxed max-w-[62ch]">{ctx.business_summary.value}</p></Disclosure></div>
        )}
      </Fact>

      <section className="py-6 border-t border-[var(--border)] grid md:grid-cols-[180px_1fr] gap-2 md:gap-6" data-testid="business-products">
        <div><Eyebrow>Products</Eyebrow><p className="mt-2 text-[12.5px] text-[var(--text-tertiary)] max-w-[22ch]">Derived from your business description.</p></div>
        <div className="min-w-0">
          <div className="flex flex-wrap gap-2">
            {profile.products?.length ? profile.products.map((p) => <Chip key={p} tone="accent">{p}</Chip>) : <p className="text-[14.5px] text-[var(--text-tertiary)]">Martian couldn't identify products from the current description. Add them to “Who we are” if useful.</p>}
            {profile.category && <p className="w-full mt-2 text-[13px] text-[var(--text-secondary)]">Detected category: {profile.category}</p>}
          </div>
          <p className="mt-3 text-[12px] leading-relaxed text-[var(--text-tertiary)]">This is a summary inferred from the description, not a separate confirmed product catalogue. Use <span className="font-medium">Correct</span> under “Who we are” to improve it.</p>
        </div>
      </section>

      <Fact label="Customers" field={ctx.audience} fieldKey="audience" onSave={save} onConfirm={confirm}>
        <div className="flex flex-wrap gap-2">{(profile.segments || []).map((s) => <Chip key={s}>{s}</Chip>)}</div>
        <div className="mt-2"><Disclosure label="How Martian describes them"><p className="text-[14px] text-[var(--text-secondary)] leading-relaxed max-w-[62ch]">{ctx.audience?.value}</p></Disclosure></div>
        <p className="mt-3 text-[12px] leading-relaxed text-[var(--text-tertiary)]">The segment chips are derived from the audience description above. Correct that description if they are inaccurate; they are not independently verified customer records.</p>
      </Fact>

      <Fact label="Main market" field={ctx.primary_market} fieldKey="primary_market" onSave={save} onConfirm={confirm}>
        <p className="text-[16px] font-semibold">{ctx.primary_market?.value}</p>
      </Fact>

      <Fact label="Markets served" field={ctx.markets_served} fieldKey="markets_served" onSave={save} onConfirm={confirm} list>
        <div className="flex flex-wrap gap-2">{(ctx.markets_served?.value || []).map((m) => <Chip key={m}>{m}</Chip>)}</div>
      </Fact>

      <Fact label="Positioning" field={ctx.positioning} fieldKey="positioning" onSave={save} onConfirm={confirm}>
        <p className="text-[15px] leading-relaxed max-w-[62ch]">{ctx.positioning?.value}</p>
      </Fact>

      <Fact label="Additional context" field={ctx.additional_context} fieldKey="additional_context" onSave={save} onConfirm={confirm}>
        <p className="text-[14.5px] leading-relaxed max-w-[62ch] whitespace-pre-wrap">{ctx.additional_context?.value}</p>
      </Fact>

      <Competitors workspaceId={workspaceId} />

      <Fact label="Growth priorities" field={ctx.growth_priorities} fieldKey="growth_priorities" onSave={save} onConfirm={confirm} list>
        <ul className="space-y-1.5 list-disc pl-5 text-[14.5px] max-w-[62ch]">{(ctx.growth_priorities?.value || []).map((g) => <li key={g}>{g}</li>)}</ul>
      </Fact>

      {/* The watchlist is part of the business: what Martian watches for you */}
      <section className="py-6 border-t border-[var(--border)] grid md:grid-cols-[180px_1fr] gap-2 md:gap-6" data-testid="business-watchlist-link">
        <Eyebrow>Questions Martian watches</Eyebrow>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-[14.5px]">{questions.length} question{questions.length === 1 ? '' : 's'} · {approvedQuestions ? `${approvedQuestions} approved` : 'none approved yet'}</p>
          <SecondaryButton to={fp('/business/watchlist')}>Open watchlist</SecondaryButton>
        </div>
      </section>

      <section className="mt-8 border-t border-[var(--border)] pt-6" data-testid="business-arc-model">
        <div className="flex items-start gap-3">
          <ARCState state="thinking" size={44} showLabel label="Reasoning flow" announce={false} className="shrink-0" />
          <div className="min-w-0">
            <Eyebrow>How the context fits</Eyebrow>
            <h2 className="mt-1 font-display text-[18px] font-semibold text-[var(--text-primary)]">Business context is the foundation, not the outcome.</h2>
            <p className="mt-1 max-w-[68ch] text-[12.5px] leading-relaxed text-[var(--text-secondary)]">ARC maps the reasoning flow from a signal to learning. This is the system model, not a claim that every stage is currently populated or connected for this workspace.</p>
          </div>
        </div>
        <div className="arc-chain-host mt-4 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4" data-testid="business-chain-host">
          <IntelligenceChain compact label="ARC reasoning model" />
        </div>
      </section>

      {lastUpdated && <p className="pt-4 text-[12.5px] text-[var(--text-tertiary)]">Last updated {formatDate(lastUpdated)}</p>}
    </div>
  );
}
